/**
 * The skyPerformance vocabulary and configuration contract (spec §4.3, analysis §A.5, §D item 9).
 *
 * Keys are fixed in code; labels, lists and thresholds are data a vertical pack supplies and an
 * instance may override (`perf_settings`). Nothing here is a function the hire product reads
 * (ADR-0006 amendment): a pack is pure, versioned data.
 */
import { z } from "zod";
import { PERFORMANCE_ROLE_NAMES, type PerformanceRoleName } from "../roleNames";
import { ROLLOUT_LIMITS } from "./rollout";

export const ROLE_KEYS = ["employee", "manager", "hr"] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

/** Order is the comparison order: `staff < dept < community < regional < corporate`. */
export const LEVEL_KEYS = ["staff", "dept", "community", "regional", "corporate"] as const;
export type LevelKey = (typeof LEVEL_KEYS)[number];

/**
 * The level from which a person reaches past one community. The level ceiling holds both ways:
 * only whoever may grant perf_hr (D6′: perf_hr or hire admin) may set a level at or above it, or
 * change anything for a person already there (9.c). The server enforces it
 * (server/products/performance/services/ceilings.ts); the Users drawer reads it to decide what to
 * offer.
 */
export const LEVEL_CEILING_FROM = "regional" as const satisfies LevelKey;

export const TERM_KEYS = ["coaching", "pip", "pipShort", "eval", "visit", "site"] as const;
export type TermKey = (typeof TERM_KEYS)[number];

/** The wireframe's three standing lines on every plan (`views-pip.js:250-255`, D22). */
export const DEFAULT_PLAN_SUPPORT = [
  "Employee Assistance Program, 24 hour hotline",
  "Reasonable accommodation, through your supervisor and HR",
  "FMLA, paid sick leave and short term disability guidance",
] as const;

/**
 * PR #1314 (vocabulary from the pack): the neutral wording a stored config falls back to when it
 * predates these paths. The packs carry their own (base.ts, senior-living.ts, generic-office.ts).
 */
export const DEFAULT_PLAN_LEVEL_HEADING = "Level of discipline";
export const DEFAULT_PLAN_ACTION_EXAMPLE = "Meet every deadline, or flag it a day ahead";
export const DEFAULT_RETENTION_MATCHING_RECORDS = "the records you keep longest";

export const COACHING_FORMS = ["eval", "visit"] as const;

const levelKey = z.enum(LEVEL_KEYS);
const perfRoleName = z.enum([
  PERFORMANCE_ROLE_NAMES.employee,
  PERFORMANCE_ROLE_NAMES.manager,
  PERFORMANCE_ROLE_NAMES.hr,
]);

const label = z.string().min(1).max(60);
// Spelled out rather than built from the key arrays, so z.infer gives the exact key set without a cast.
// Strict: a label for a key the code does not know (`levels.vp`) is a malformed pack, not noise to strip.
const roleLabels = z.strictObject({ employee: label, manager: label, hr: label });
const levelLabels = z.strictObject({
  staff: label,
  dept: label,
  community: label,
  regional: label,
  corporate: label,
});
const termLabels = z.strictObject({
  coaching: label,
  pip: label,
  pipShort: label,
  eval: label,
  visit: label,
  site: label,
});

/** A list whose entries are addressed by `key` must not repeat one. */
function uniqueKeys<T extends { key: string }>(items: readonly T[]): boolean {
  return new Set(items.map((item) => item.key)).size === items.length;
}
const UNIQUE_KEYS = { message: "Duplicate key" } as const;

export const coachingTypeSchema = z.object({
  // At most 40, as the Settings drawer allows (CoachingTypeDrawer.tsx; PR #1314 pack P2-5).
  key: z
    .string()
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().min(1).max(60),
  description: z.string().max(200),
  /** One entry filed to several people at once (team meeting). */
  multi: z.boolean().default(false),
  /** Opens a form instead of a text box. */
  form: z.enum(COACHING_FORMS).nullable().default(null),
  /** The level a manager needs to pick this type; null = any manager. */
  minLevel: levelKey.nullable().default(null),
  /** Placeholder for the record's title (E1 §3.2); pack data, never keyed on the type in code. */
  titleHint: z.string().max(120).default(""),
  /** Placeholder for "what happened". */
  prompt: z.string().max(300).default(""),
  /** `good` reads as positive (recognition): its own glance count and badge tint. */
  tone: z.enum(["good", "neutral"]).default("neutral"),
  /**
   * Whether acknowledging a record of this type may come with "I disagree" (meeting 2026-10-19):
   * HR switches it off for a mandatory policy. Defaulted on, so every type stored before keeps
   * disagreeing. A positive type never offers it, whatever this says (offersDisagree).
   */
  allowDisagree: z.boolean().default(true),
  /**
   * E9: hidden from the picker and refused for new filings; records already filed keep the type,
   * its name and its counts. A type is never deleted (records reference its key). Defaulted, so
   * every type stored before E9 is active.
   */
  retired: z.boolean().default(false),
});

/**
 * One offence type on a plan. `key` is stable; `name` is the label HR may change. The entry that
 * asks for a description is marked by `freeText` (the "Other" row), never found by its label, so a
 * renamed or translated "Other" keeps its text box and the server's check (PR #1314 ruling).
 * Plans store keys; a plan saved before keys holds the names then picked (0130_perf_plans.sql),
 * read back through resolveOffence (offences.ts), so names stay unique too.
 */
export const offenceTypeSchema = z.object({
  key: z
    .string()
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().trim().min(1).max(60),
  freeText: z.boolean().default(false),
});

const offenceTypesSchema = z
  .array(offenceTypeSchema)
  .min(1)
  .refine(uniqueKeys, UNIQUE_KEYS)
  .refine((items) => new Set(items.map((t) => t.name)).size === items.length, {
    message: "Duplicate name",
  })
  .refine((items) => items.filter((t) => t.freeText).length <= 1, {
    message: "At most one offence type asks for a description",
  });

/**
 * E9: how long a terminated person's file stays searchable after its export. "unset" is a state
 * (the period is still to be confirmed), never null: null means "inherit the pack value" in
 * saveSettings. Stored only; the purge that enforces it arrives with Records (E8).
 */
export const RETENTION_PERIODS = ["unset", "2y", "7y", "10y", "indefinite"] as const;
export type RetentionPeriod = (typeof RETENTION_PERIODS)[number];

export const NO_HR_DEFAULT_MAPPING =
  "A pack's default position mapping may not grant perf_hr; HR maps HR positions by hand.";

/**
 * A pack's default position → role/level. Never `perf_hr`: that role is the performance
 * administrator (everyone's records, settings, exports), so a title glob such as "hr *" would hand
 * it to an "HR Intern" with no human review. perf_hr comes only from a mapping HR saves by hand,
 * an override, or provisioning.
 */
export const positionMappingSchema = z
  .object({
    /** Lower-case glob over the normalised job title; `*` is the only wildcard. */
    pattern: z.string().min(1).max(120),
    roleName: perfRoleName,
    levelKey: levelKey,
  })
  .refine((m) => m.roleName !== PERFORMANCE_ROLE_NAMES.hr, {
    message: NO_HR_DEFAULT_MAPPING,
    path: ["roleName"],
  });

const count = z.number().int().nonnegative();

/** E5 R1: one checklist (sections of questions, grouped) and its Financial tab's blocks. */
const visitText = z.string().trim().min(1).max(500);
export const visitFormSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
  name: z.string().trim().min(1).max(80),
  sections: z
    .array(
      z.object({
        key: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
        title: z.string().trim().min(1).max(120),
        group: z.string().trim().min(1).max(80),
        items: z.array(visitText).max(200),
      }),
    )
    .max(60),
  financial: z
    .array(
      z.object({
        key: z.string().regex(/^[a-z][a-z0-9_]{0,47}$/),
        title: z.string().trim().min(1).max(120),
        type: z.enum(["table", "scan", "metrics", "entry", "note"]),
        hint: z.string().max(200).default(""),
        rows: z.array(visitText).max(60).optional(),
        items: z.array(visitText).max(60).optional(),
        /** An entry block whose items join the action plan ("Action items out of this call"). */
        toPlan: z.boolean().optional(),
      }),
    )
    .max(40)
    .default([]),
});
export type VisitForm = z.infer<typeof visitFormSchema>;

export const performanceConfigSchema = z.object({
  /** Bumped when the shape of this object changes; packs declare the version they were written for. */
  version: z.literal(1),
  vertical: z.string().min(1),
  org: z.object({
    /** Null in a pack: the resolver fills it from the instance registry. */
    name: z.string().max(120).nullable(),
    shortName: z.string().max(40).nullable(),
    hris: z.string().max(80).nullable(),
  }),
  roles: roleLabels,
  levels: levelLabels,
  terms: termLabels,
  visits: z.object({
    /** Who may run the site-visit form; null = the feature does not apply to this vertical. */
    runMinLevel: levelKey.nullable(),
    /** E5 R1: the visit forms, every part HR-configurable (Settings, Visit forms). */
    forms: z.array(visitFormSchema).default([]),
  }),
  evaluations: z.object({
    flagBelowPct: z.number().int().min(0).max(100),
    scale: z.array(z.object({ n: z.number().int(), name: z.string(), sub: z.string() })).min(2),
    sections: z
      .array(
        z.object({
          key: z.string(),
          name: z.string(),
          questions: z.array(z.string()),
          kpi: z.boolean().default(false),
          dev: z.boolean().default(false),
        }),
      )
      .refine(uniqueKeys, UNIQUE_KEYS),
    kpiCounts: z.object({ year1: count, year2: count }),
    maxScores: z.object({ year1: count, year2: count }),
    /** E4 R2: the fewest development opportunities an evaluation completes with. */
    minDevelopment: z.number().int().min(0).default(3),
  }),
  plans: z.object({
    activeMonths: z.number().int().positive(),
    reminderDays: z.array(z.number().int().positive()).min(1),
    maxActions: z.number().int().positive(),
    maxReviews: z.number().int().positive(),
    levels: z
      .array(z.object({ key: z.string(), name: z.string(), letter: z.boolean() }))
      .min(1)
      .refine(uniqueKeys, UNIQUE_KEYS),
    offenceTypes: offenceTypesSchema,
    // E3 (spec §4.2). Defaults so a stored config from before E3 still parses (`version` stays 1).
    /** O-2: how many levels up the starter's reporting line approve before HR. */
    approvalLevels: z.number().int().min(1).max(4).default(2),
    /** D22 (R10): the standing support lines on every plan, as labels a vertical can change. */
    support: z
      .array(z.string().max(200))
      .max(10)
      .default([...DEFAULT_PLAN_SUPPORT]),
    /** D15 (R16): a new plan's reminder lead, in days. */
    remindDaysDefault: z.number().int().positive().default(7),
    /** The plan screens' name for `levels` ("Level of discipline"). */
    levelHeading: label.default(DEFAULT_PLAN_LEVEL_HEADING),
    /** The first required action's placeholder: an example in this vertical's words. */
    actionExample: z.string().min(1).max(200).default(DEFAULT_PLAN_ACTION_EXAMPLE),
  }),
  coachingTypes: z.array(coachingTypeSchema).min(1).refine(uniqueKeys, UNIQUE_KEYS),
  visitScale: z.array(z.object({ n: z.number().int(), label: z.string() })),
  syncFields: z.array(z.string()),
  writeBack: z.array(z.object({ what: z.string(), enabled: z.boolean() })),
  defaultPositionMappings: z.array(positionMappingSchema),
  retention: z
    .object({
      period: z.enum(RETENTION_PERIODS).default("unset"),
      /** What the 10-year choice matches ("clinical and resident records"); pack wording. */
      matchingRecords: z.string().min(1).max(80).default(DEFAULT_RETENTION_MATCHING_RECORDS),
    })
    .default({ period: "unset", matchingRecords: DEFAULT_RETENTION_MATCHING_RECORDS }),
  /**
   * Spec 2026-10-19 §1 (D1, D2): the roster locations where Performance Management is live. Null
   * (the default, and every pack's value) is everyone, so a config stored before this parses
   * unchanged. Strict: an unknown key under `rollout` is refused. Saved only through
   * PUT /settings/rollout, which recomputes roles (plan ruling R6).
   */
  rollout: z
    .strictObject({
      liveLocations: z
        .array(z.string().trim().min(1).max(ROLLOUT_LIMITS.locationMax))
        .max(ROLLOUT_LIMITS.locationsMax)
        .nullable()
        .default(null),
    })
    .default({ liveLocations: null }),
});

export type PerformanceConfig = z.infer<typeof performanceConfigSchema>;
export type CoachingType = z.infer<typeof coachingTypeSchema>;
export type OffenceType = z.infer<typeof offenceTypeSchema>;
export type DefaultPositionMapping = z.infer<typeof positionMappingSchema>;

/** `level` is at or above `min`; a null threshold admits nobody (the feature does not apply). */
export function levelAtLeast(level: LevelKey, min: LevelKey | null): boolean {
  if (min === null) return false;
  return LEVEL_KEYS.indexOf(level) >= LEVEL_KEYS.indexOf(min);
}

const ROLE_KEY_BY_NAME: Record<PerformanceRoleName, RoleKey> = {
  perf_employee: "employee",
  perf_manager: "manager",
  perf_hr: "hr",
};

export function roleKeyOf(name: PerformanceRoleName): RoleKey {
  return ROLE_KEY_BY_NAME[name];
}

export function roleNameOf(key: RoleKey): PerformanceRoleName {
  return PERFORMANCE_ROLE_NAMES[key];
}

/** Lower-case glob match; `*` matches any run of characters, everything else is literal. */
export function matchesPosition(pattern: string, positionKey: string): boolean {
  const escaped = pattern
    .toLowerCase()
    .split("*")
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join(".*");
  return new RegExp(`^${escaped}$`).test(positionKey.toLowerCase());
}

const hasOwn = (target: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(target, key);
/** Path segments that reach the prototype chain rather than config data. */
const UNSAFE_SEGMENTS = new Set(["__proto__", "constructor", "prototype"]);
/** Which pack and schema version apply is decided by the registry and the code, never by an override. */
const FIXED_ROOTS = new Set(["vertical", "version"]);

/**
 * Overlay dotted-path overrides on a config and re-validate the whole. A key that is not a path
 * into the schema, that targets `vertical` or `version`, or whose value fails validation, is
 * reported in `rejected` and not applied; every other override lands. The input config is
 * validated first (throws a ZodError if it is not a valid config) and never mutated.
 */
export function applyOverrides(
  config: PerformanceConfig,
  overrides: Record<string, unknown>,
): { config: PerformanceConfig; rejected: string[] } {
  // Parsing builds fresh objects, strips stray keys and refuses an invalid input outright.
  let current: PerformanceConfig = performanceConfigSchema.parse(config);
  const rejected: string[] = [];
  for (const [path, value] of Object.entries(overrides)) {
    const candidate = structuredClone(current) as unknown as Record<string, unknown>;
    const parts = path.split(".");
    let cursor: Record<string, unknown> = candidate;
    let ok = !FIXED_ROOTS.has(parts[0]) && !parts.some((part) => UNSAFE_SEGMENTS.has(part));
    for (const part of ok ? parts.slice(0, -1) : []) {
      if (!hasOwn(cursor, part)) {
        ok = false;
        break;
      }
      const next = cursor[part];
      if (typeof next !== "object" || next === null || Array.isArray(next)) {
        ok = false;
        break;
      }
      cursor = next as Record<string, unknown>;
    }
    const leaf = parts[parts.length - 1];
    if (!ok || !hasOwn(cursor, leaf)) {
      rejected.push(path);
      continue;
    }
    cursor[leaf] = value;
    const parsed = performanceConfigSchema.safeParse(candidate);
    if (!parsed.success) {
      rejected.push(path);
      continue;
    }
    current = parsed.data;
  }
  return { config: current, rejected };
}
